import { QueryClient } from '@tanstack/react-query';
import { config } from '@/config';
import { ApiError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: config.staleTimeMs,
      refetchOnWindowFocus: false,
      // An expired session or a permission failure will not fix itself.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status < 500 ? false : failureCount < 2,
    },
  },
});

/** Every query key in one place, so invalidation never has to guess. */
export const keys = {
  session: ['session'] as const,

  workspaces: ['workspaces'] as const,
  organizations: ['organizations'] as const,
  organization: (id: string) => ['organizations', id] as const,
  organizationWorkspaces: (id: string) => ['organizations', id, 'workspaces'] as const,
  organizationFolders: (id: string) => ['organizations', id, 'folders'] as const,
  organizationDocs: (id: string) => ['organizations', id, 'docs'] as const,
  organizationDashboards: (id: string) => ['organizations', id, 'dashboards'] as const,
  organizationWorkflows: (id: string) => ['organizations', id, 'workflows'] as const,
  organizationPeople: (id: string) => ['organizations', id, 'mentionable'] as const,
  boards: (workspaceId: string) => ['workspaces', workspaceId, 'boards'] as const,
  folders: (workspaceId: string) => ['workspaces', workspaceId, 'folders'] as const,
  docs: (workspaceId: string) => ['workspaces', workspaceId, 'docs'] as const,
  doc: (docId: string) => ['docs', docId] as const,
  docComments: (docId: string) => ['docs', docId, 'comments'] as const,
  dashboards: (workspaceId: string) => ['workspaces', workspaceId, 'dashboards'] as const,
  dashboard: (dashboardId: string) => ['dashboards', dashboardId] as const,
  dashboardData: (dashboardId: string, boardIds: string[]) =>
    ['dashboards', dashboardId, 'data', boardIds.join(',')] as const,
  boardAccess: (boardId: string) => ['boards', boardId, 'access'] as const,
  board: (boardId: string) => ['boards', boardId, 'view'] as const,
  projectOverview: (boardId: string) => ['boards', boardId, 'overview'] as const,
  boardColumns: (boardId: string) => ['boards', boardId, 'columns'] as const,
  boardGroups: (boardId: string) => ['boards', boardId, 'groups'] as const,
  workspaceWorkflows: (workspaceId: string) => ['workspaces', workspaceId, 'workflows'] as const,
  workflows: ['workflows', 'all'] as const,
  workflow: (id: string) => ['workflows', id] as const,
  workflowRuns: (id: string) => ['workflows', id, 'runs'] as const,
  workflowCatalog: ['workflows', 'catalog'] as const,
  workflowBoards: ['workflows', 'boards'] as const,
  templates: ['templates'] as const,
  boardForms: (boardId: string) => ['boards', boardId, 'forms'] as const,
  myTasks: ['my-tasks'] as const,
  timer: ['timer', 'status'] as const,
  queueToday: ['queue', 'today'] as const,
  myTimeLogs: (from: string, to: string) => ['time-logs', 'my', from, to] as const,
  timerRequests: ['timer-requests', 'my'] as const,
  timerRequestQueue: ['timer-requests', 'queue'] as const,
  reportTime: (workspaceId: string, from: string, to: string) =>
    ['reports', 'time', workspaceId, from, to] as const,
  reportStatus: (boardId: string) => ['reports', 'status', boardId] as const,

  attendanceToday: ['attendance', 'today'] as const,
  attendanceHistory: (from: string, to: string) => ['attendance', 'history', from, to] as const,
  attendanceTeam: (date: string) => ['attendance', 'team', date] as const,

  leaveTypes: ['leave', 'types'] as const,
  leaveRequests: ['leave', 'requests'] as const,
  leaveQueue: ['leave', 'queue'] as const,
  leaveBalances: (year?: number) => ['leave', 'balances', year ?? 'current'] as const,
  leaveBalancesAll: (year?: number) => ['leave', 'balances', 'all', year ?? 'current'] as const,

  employees: (filter: Record<string, unknown>) => ['employees', filter] as const,
  employee: (userId: string) => ['employees', userId] as const,
  pmOwnership: (userId: string, to: string) => ['employees', userId, 'pm-ownership', to] as const,
  teams: ['employees', 'teams'] as const,
  departmentOptions: ['employees', 'departments'] as const,
  departments: ['departments'] as const,
  departmentCandidates: ['departments', 'candidates'] as const,
  pmPeople: ['pm', 'people'] as const,

  hrSettings: ['hr', 'settings'] as const,
  notificationSettings: ['hr', 'settings', 'notifications'] as const,
  leaveTypesAll: ['leave', 'types', 'all'] as const,
  holidays: ['hr', 'holidays'] as const,

  roles: ['admin', 'roles'] as const,
  permissions: ['admin', 'permissions'] as const,

  notifications: ['notifications'] as const,
  notificationCount: ['notifications', 'count'] as const,
  emailPreferences: ['email-preferences'] as const,
};

/**
 * Where a doc, dashboard, workflow or folder lives: a workspace, or an
 * organization's own level (no workspace). Rows made inside a workspace
 * carry its organization too, but the workspace is what they answer to.
 */
export interface Home {
  workspaceId: string | null;
  organizationId: string | null;
}

/** The lists that show what lives in a home - what to refresh after a change there. */
export function homeLists(home: Home) {
  if (home.workspaceId) {
    return {
      folders: keys.folders(home.workspaceId),
      docs: keys.docs(home.workspaceId),
      dashboards: keys.dashboards(home.workspaceId),
      workflows: keys.workspaceWorkflows(home.workspaceId),
    };
  }
  const id = home.organizationId ?? '';
  return {
    folders: keys.organizationFolders(id),
    docs: keys.organizationDocs(id),
    dashboards: keys.organizationDashboards(id),
    workflows: keys.organizationWorkflows(id),
  };
}

/** What a new item sends to say where it goes: exactly one of the two. */
export const homeBody = (home: Home) =>
  (home.workspaceId ? { workspaceId: home.workspaceId } : { organizationId: home.organizationId ?? undefined });
